import { useRouter } from 'next/router'
import React, { createContext, useContext, useReducer, useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { createDiscount, editDiscountById } from 'src/lib/core/api-methods'
import { successToast, errorToast } from 'src/lib/utils/toasters'
import {
  DiscountFormV2StateType,
  CustomerEligibilityV2Type,
  DiscountStackMode,
  DiscountMethod,
  CouponType,
  AmountType,
  bxgyProductStrategy,
  generationSourceType,
  UiConfigDiscountType,
  DiscountApplyToType,
  ApplicationTarget,
  PrerequisiteType
} from 'src/lib/types/discountsV2'
import { createDiscountPayloadV2 } from 'src/lib/utils/discounts/discountsV2Helpers'
import { getDiscountApplicationRules } from 'src/lib/utils/discounts/discountsV2Parser'
import { getDiscountSchema } from 'src/@core/schemas/discounts/discountSchema'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import useGTM from 'src/lib/hooks/useGTM'
import { getDiscountTypeForGTM } from 'src/lib/utils/discountHelpers'

type Action = {
  type: 'SET_FORM_STATE'
  payload: Partial<DiscountFormV2StateType>
}

const initialState: DiscountFormV2StateType = {
  uiConfig: {
    type: UiConfigDiscountType.BUNDLE
  },
  prerequisite: {
    type: CouponType.BUNDLE,
    customerEligibility: {
      type: CustomerEligibilityV2Type.ALL_CUSTOMERS
    },
    usageLimit: {
      maxApplicableCount: undefined,
      maxApplicablePerUserCount: undefined
    },
    stackability: {
      stackMode: DiscountStackMode.ALL
    },
    availability: {
      active: false,
      enabledChannelIds: [],
      startDate: Date.now(),
      endDate: null,
      discoverable: true
    }
  },
  entitlement: {
    type: CouponType.BUNDLE,
    entitledProductCount: 0,
    concessionAmount: 0,
    amountType: AmountType.FLAT,
    generationSource: generationSourceType.SINGLE,
    applicationTarget: ApplicationTarget.ALL,
    entitledCatalogue: [],
    excludedCatalogue: [],
    metaData: {
      excludedCatalogue: [],
      entitledCatalogue: []
    },
    applicationMethod: {
      method: DiscountMethod.CODE,
      autoApplicable: false,
      discountCode: '',
      title: ''
    },
    maxUsesPerOrder: null,
    applyTo: DiscountApplyToType.ALL_PRODUCTS
  },
  configuration: {
    preview: {
      header: '',
      description: '',
      longDescription: ''
    },
    couponType: CouponType.BUNDLE
  },
  errors: {}
}

const discountFormReducer = (state: DiscountFormV2StateType, action: Action): DiscountFormV2StateType => {
  switch (action.type) {
    case 'SET_FORM_STATE':
      return {
        ...state,
        ...action.payload
      }
    default:
      return state
  }
}

export interface DiscountContextType {
  state: DiscountFormV2StateType
  isDirty: boolean
  actions: {
    setFormState: (payload: Partial<DiscountFormV2StateType>, clearPaths?: string[]) => void
    markSaved: (savedState: DiscountFormV2StateType) => void
    handleSave: (overrideState?: DiscountFormV2StateType) => void
    validateForm: (stateToValidate?: DiscountFormV2StateType) => boolean
    clearError: (path: string) => void
    registerRef: (path: string, node: HTMLElement | null) => void
  }
}

const DiscountFormContext = createContext<DiscountContextType | undefined>(undefined)

export const DiscountFormProvider: React.FC<{ children: React.ReactNode; initialDiscountType?: UiConfigDiscountType; onSaveSuccess?: (createdDiscount: any) => void }> = ({ children, initialDiscountType, onSaveSuccess }) => {
  const computedInitialState: DiscountFormV2StateType = useMemo(() => {
    if (!initialDiscountType) return initialState
    const rulesState = getDiscountApplicationRules(initialDiscountType)
    return {
      ...initialState,
      ...rulesState,
      prerequisite: { ...initialState.prerequisite, ...rulesState.prerequisite },
      entitlement: { ...initialState.entitlement, ...rulesState.entitlement },
      configuration: { ...initialState.configuration, ...rulesState.configuration },
    }
  }, [])
  const [state, dispatch] = useReducer(discountFormReducer, computedInitialState)
  const {
    state: { merchant }
  } = useMerchant()
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  // Ref Registry
  const fieldRefs = useRef<Map<string, HTMLElement>>(new Map())
  const savedStateRef = useRef<DiscountFormV2StateType | null>(null)

  const markSaved = useCallback((savedState: DiscountFormV2StateType) => {
    savedStateRef.current = savedState
  }, [])

  const registerRef = useCallback((path: string, node: HTMLElement | null) => {
    if (node) {
      fieldRefs.current.set(path, node)
    } else {
      fieldRefs.current.delete(path)
    }
  }, [])

  const router = useRouter()
  const { discountId } = router.query

  const isDirty = savedStateRef.current === null
    ? !discountId
    : JSON.stringify(state) !== JSON.stringify(savedStateRef.current)

  const setFormState = useCallback(
    (payload: Partial<DiscountFormV2StateType>, clearPaths?: string[]) => {
      let errorsPayload: { errors?: Record<string, string> } = {}

      if (clearPaths && clearPaths.length > 0 && Object.keys(state.errors).length > 0) {
        const newErrors = { ...state.errors }
        let hasChanges = false

        clearPaths.forEach(path => {
          if (newErrors[path]) {
            delete newErrors[path]
            hasChanges = true
          }
        })

        if (hasChanges) {
          errorsPayload = { errors: newErrors }
        }
      }

      dispatch({
        type: 'SET_FORM_STATE',
        payload: {
          ...payload,
          ...errorsPayload
        }
      })
    },
    [state.errors]
  )

  const clearError = useCallback(
    (path: string) => {
      const newErrors = { ...state.errors }
      delete newErrors[path]
      setFormState({ errors: newErrors })
    },
    [state.errors, setFormState]
  )

  const validateForm = useCallback(
    (stateToValidate?: DiscountFormV2StateType) => {
      const currentState = stateToValidate || state
      const schema = getDiscountSchema(currentState.uiConfig.type)
      const result = schema.safeParse(currentState)

      if (!result.success) {
        const newErrors: Record<string, string> = {}
        result.error.issues.forEach(issue => {
          const path = issue.path.join('.')
          newErrors[path] = issue.message
        })

        dispatch({
          type: 'SET_FORM_STATE',
          payload: { errors: newErrors }
        })

        setTimeout(() => {
          const errorPaths = Object.keys(newErrors)
          const errorNodes = errorPaths
            .map(path => fieldRefs.current.get(path))
            .filter((node): node is HTMLElement => !!node)

          if (errorNodes.length > 0) {
            // Sort nodes by document position to find the first one in the DOM
            errorNodes.sort((a, b) => {
              return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
            })

            const firstElement = errorNodes[0]
            firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)

        return false
      }

      // Clear all errors on success
      dispatch({
        type: 'SET_FORM_STATE',
        payload: { errors: {} }
      })
      return true
    },
    [state]
  )

  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()

  const handleSave = async (overrideState?: DiscountFormV2StateType) => {
    const currentState = overrideState || state

    if (!validateForm(currentState)) {
      return
    }

    const discountPayload = createDiscountPayloadV2(currentState, merchant, Boolean(discountId))

    if (discountId) {
      sendGTMTrigger('discount_edit_completed_v2', 'edit')
    } else {
      sendGTMTrigger('discount_creation_completed_v2', 'create')
    }

    try {
      let response: any
      if (discountId) {
        response = await editDiscountById(issuerId, merchant?.accountId, discountId as string, discountPayload)
      } else {
        response = await createDiscount(issuerId, merchant?.accountId, discountPayload)
      }

      if (response?.error) {
        errorToast(response.error)
        return
      }

      const activityLogPayload = {
        entityName: EntityName.DISCOUNTS,
        actionName: discountId ? 'edited coupon' : 'created coupon',
        metadata: discountPayload
      }

      sendActivityLog(activityLogPayload)

      if (discountId) {
        sendGTMTrigger('discount_edit_completed_v2', 'edit')
      } else {
        sendGTMTrigger('discount_creation_completed_v2', 'save')
      }

      successToast(discountId ? 'Discount updated successfully' : 'Discount created successfully')

      if (onSaveSuccess) {
        onSaveSuccess(response?.data?.data ?? response?.data ?? null)
        return
      }

      const isExitCoupon = currentState.uiConfig.type === UiConfigDiscountType.EXIT_COUPON
      const returnUrl = router.query.returnUrl as string | undefined
      router.push(returnUrl ? decodeURIComponent(returnUrl) : isExitCoupon ? '/discounts#exit_coupons' : '/discounts')
    } catch (error) {
      console.error(error)
      errorToast('Something went wrong')
    }
  }

  const value = useMemo(
    () => ({
      state,
      isDirty,
      actions: { setFormState, markSaved, handleSave, validateForm, clearError, registerRef }
    }),
    [state, isDirty, setFormState, markSaved, validateForm, clearError, registerRef]
  )

  return <DiscountFormContext.Provider value={value}>{children}</DiscountFormContext.Provider>
}

export const useDiscountForm = () => {
  const context = useContext(DiscountFormContext)
  if (!context) {
    throw new Error('useDiscountForm must be used within a DiscountFormProvider')
  }
  return context
}

export const useScrollToErrorRef = (path: string) => {
  const {
    actions: { registerRef }
  } = useDiscountForm()
  const ref = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (ref.current) {
      registerRef(path, ref.current)
    }
    return () => {
      registerRef(path, null)
    }
  }, [path, registerRef])

  return ref
}
